import React, { useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import {
	deleteUserEventEntries,
	fetchListEventEntries,
	findEventMicroCMS,
} from "../../utils/helpers/api";
import { authHelper } from "../../utils/helpers";
import { useGlobalState } from "../../contexts/GlobalContext";
import { useLocation } from "react-router-dom";
import { appConfig } from "../../utils/configs";
import Pagination from "../../components/Commons/Pagination";
import QRCode from "react-qr-code";
import * as Dialog from "@radix-ui/react-dialog";
import { Routes } from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";

const Events: React.FC = () => {
	const { setModal, globalState, setStateValue, setModalDisabledConfirm } =
		useGlobalState();
	const location = useLocation();
	const navigate = useNavigate();
	const [state, setState] = useState<IEventsPageState>({
		sessionIds: [],
		actionRemove: false,
		actionId: 0,
		events: [],
		total_pages: undefined,
		total_results: undefined,
		current_page: undefined,
		searchParams: undefined,
	});
	const {
		sessionIds,
		actionRemove,
		actionId,
		events,
		total_pages,
		current_page,
		searchParams,
	} = state;

	useEffect(() => {
		const queryParams = new URLSearchParams(window.location.search);
		if (queryParams.size > 0) {
			setState((preState) => ({
				...preState,
				current_page: Number(queryParams.get("page")) ?? 1,
				searchParams: {
					...preState.searchParams,
					page: queryParams.get("page") ?? undefined,
					current_event: queryParams.get("current_event")
						? queryParams.get("current_event") === "true"
						: undefined,
				},
			}));
		}
		handleGetListevent();
	}, []);

	useEffect(() => {
		if (searchParams && current_page) {
			updateUrlWithParams();
			handleGetListevent();
		}
	}, [searchParams, current_page]);

	useEffect(() => {
		const queryParams = new URLSearchParams(window.location.search);
		if (queryParams.size > 0) {
			setState((preState) => ({
				...preState,
				current_page: Number(queryParams.get("page")) ?? 1,
				searchParams: {
					...preState.searchParams,
					page: queryParams.get("page") ?? undefined,
					current_event: queryParams.get("current_event")
						? queryParams.get("current_event") === "true"
						: undefined,
				},
			}));
		}
	}, []);

	useEffect(() => {
		if (!globalState.modalContext?.isOpen) {
			setState((preState) => ({ ...preState, sessionIds: [] }));
		}
	}, [globalState.modalContext?.isOpen]);

	useEffect(() => {
		if (actionRemove && actionId !== 0) {
			handleDeleteUserEventEntries(actionId);
			setState((preState) => ({
				...preState,
				actionRemove: false,
				actionId: 0,
			}));
		}
	}, [actionRemove, actionId]);

	const processQuery = () => {
		const params = new URLSearchParams();

		Object.entries(searchParams || {}).forEach(([key, value]) => {
			if (value !== undefined) {
				params.append(key, value.toString());
			}
		});

		return params;
	};

	const _renderItemSchedules = (
		lecture: ILecture,
		schedules: string[],
		session: ISession
	) => {
		const startDate = new Date(schedules[0] ?? "");
		const endDate = new Date(schedules[1] ?? "");

		const startStr = `${startDate
			.getHours()
			.toString()
			.padStart(2, "0")}:${startDate.getMinutes().toString().padStart(2, "0")}`;
		const endStr = `${endDate.getHours().toString().padStart(2, "0")}:${endDate
			.getMinutes()
			.toString()
			.padStart(2, "0")}`;

		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "flex-start", sm: "center" },
					gap: 1,
				}}
				key={lecture.id}
			>
				<Box sx={{ whiteSpace: "nowrap" }}>
					【{session.venue?.slice(0, 1)}
					{session.block}
					{lecture.block}】{startStr}~{endStr}　
				</Box>
				<div className="lecture-name">{lecture.name}</div>
			</Box>
		);
	};

	const isLectureExpired = (lecture: ILecture): boolean => {
		const currentDate = new Date();
		const lectureEndDate = new Date(lecture.end_at ?? "");
		// Convert both dates to UTC for comparison
		const currentUTC = new Date(
			Date.UTC(
				currentDate.getUTCFullYear(),
				currentDate.getUTCMonth(),
				currentDate.getUTCDate(),
				currentDate.getHours(),
				currentDate.getUTCMinutes(),
				currentDate.getUTCSeconds()
			)
		);

		const lectureEndUTC = new Date(
			Date.UTC(
				lectureEndDate.getUTCFullYear(),
				lectureEndDate.getUTCMonth(),
				lectureEndDate.getUTCDate(),
				lectureEndDate.getUTCHours(),
				lectureEndDate.getUTCMinutes(),
				lectureEndDate.getUTCSeconds()
			)
		);

		return lectureEndUTC < currentUTC;
	};
	const _renderItemSessions = (
		lecture: ILecture,
		schedules: string[],
		session: ISession
	) => {
		const startDate = new Date(lecture.start_at ?? "");
		const endDate = new Date(lecture.end_at ?? "");
		const isExpired = isLectureExpired(lecture);

		const startStr = `${startDate
			.getUTCHours()
			.toString()
			.padStart(2, "0")}:${startDate
			.getUTCMinutes()
			.toString()
			.padStart(2, "0")}`;
		const endStr = `${endDate
			.getUTCHours()
			.toString()
			.padStart(2, "0")}:${endDate
			.getUTCMinutes()
			.toString()
			.padStart(2, "0")}`;
		return (
			<Box sx={{ display: "flex" }} className="display-wrap" key={lecture.id}>
				<Box className={`display-time ${isExpired ? "txt-gray" : ""}`}>
					【{session.venue?.slice(0, 1)}
					{session.block}
					{lecture.block}】{startStr}~{endStr}
				</Box>
				<Box className={`display-content ${isExpired ? "txt-gray" : ""}`}>
					{lecture.name}
				</Box>
			</Box>
		);
	};

	const groupSessionsByDate = (sessions: ISession[]) => {
		const grouped: { [key: string]: ISession[] } = {};

		sessions?.forEach((session) => {
			if (
				!session.schedules?.[0] ||
				!session.lectures ||
				session.lectures.length === 0
			)
				return;

			const date = new Date(session.schedules[0]);
			const dateKey = `${date.getFullYear()}.${(date.getMonth() + 1)
				.toString()
				.padStart(2, "0")}.${date.getDate().toString().padStart(2, "0")}`;

			if (!grouped[dateKey]) {
				grouped[dateKey] = [];
			}
			grouped[dateKey].push(session);
		});

		return grouped;
	};

	const _renderCancelItemSchedules = (
		lecture: ILecture,
		schedules: string[],
		session: ISession
	) => {
		const startDate = new Date(lecture.start_at ?? "");
		const endDate = new Date(lecture.end_at ?? "");
		const isExpired = isLectureExpired(lecture);

		const startStr = `${startDate
			.getUTCHours()
			.toString()
			.padStart(2, "0")}:${startDate
			.getUTCMinutes()
			.toString()
			.padStart(2, "0")}`;
		const endStr = `${endDate
			.getUTCHours()
			.toString()
			.padStart(2, "0")}:${endDate
			.getUTCMinutes()
			.toString()
			.padStart(2, "0")}`;
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "flex-start", sm: "center" },
					gap: 1,
				}}
			>
				<Box
					sx={{ whiteSpace: "nowrap" }}
					className={isExpired ? "txt-gray" : ""}
				>
					【{session.venue?.slice(0, 1)}
					{session.block}
					{lecture.block}】{startStr}~{endStr}　
				</Box>
				<div className={`lecture-name ${isExpired ? "txt-gray" : ""}`}>
					{lecture.name}
				</div>
			</Box>
		);
	};

	const updateUrlWithParams = () => {
		const query = processQuery();
		const newUrl = `${window.location.pathname}?${query.toString()}`;
		window.history.pushState({}, "", newUrl);
	};

	const checkShowButtonVideo = (
		ondemand: boolean,
		video_available_at: string,
		video_disable_at: string,
		endDate: Date,
		video_player_tag: string
	) => {
		const currentDate = new Date();
		const video_available = new Date(video_available_at ?? "");
		const video_disable = new Date(video_disable_at ?? "");
		if (!ondemand) {
			return 3;
		} else {
			if (video_available > currentDate) {
				return 4;
			}
			if (
				video_available < currentDate &&
				video_disable > currentDate &&
				endDate > currentDate &&
				video_player_tag
			) {
				return 1;
			}
			if (
				video_available < currentDate &&
				video_disable > currentDate &&
				!video_player_tag
			) {
				return 2;
			}
			if (video_disable < currentDate && endDate < currentDate) {
				return 3;
			}
			if (video_disable > currentDate && endDate < currentDate) {
				return 1;
			}
			return 3;
		}
	};

	const ShowVideo = (session_id: number) => {
		setStateValue("loader", true);
		try {
			navigate(
				Routes.SESSION_VIDEO.replace(":session_id", session_id.toString())
			);
		} catch (error) {
		} finally {
			setStateValue("loader", false);
		}
	};

	const formatDigits = (qr_id: number, flag: boolean): string => {
		const num = Math.max(0, Math.floor(qr_id));
		const padded = num.toString().padStart(flag ? 5 : 10, "0");
		return padded.slice(-1 * (flag ? 5 : 10));
	};

	const handleGetListevent = async () => {
		const query = processQuery();
		const acessToken = authHelper.accessToken();
		setStateValue("loader", true);
		setState((preState) => ({
			...preState,
			events: [],
		}));
		try {
			const result = await fetchListEventEntries(acessToken, query.toString());
			if (result.statusCode === 200) {
				setState((preState) => ({
					...preState,
					events: result.data.results,
					total_pages: result.data.total_pages,
					total_results: result.data.total_results,
					current_page: result.data.current_page,
				}));
			}

			if (result.statusCode === 500) {
				setState((preState) => ({
					...preState,
					events: [],
					total_pages: 0,
					total_results: 0,
					current_page: 1,
				}));
			}
		} catch (err) {
			return err;
		} finally {
			setStateValue("loader", false);
		}
	};

	const handleDeleteUserEventEntries = async (eventId: number) => {
		const acessToken = authHelper.accessToken();
		setStateValue("loader", true);
		try {
			const result = await deleteUserEventEntries(
				acessToken,
				sessionIds.join(",") ?? ""
			);
			if (result.statusCode === 200) {
				let checkSession = false;
				const updatedEvents = events
					.map((event) => {
						if (event.id === eventId) {
							event.sessions = event?.sessions?.filter(
								(session) => !sessionIds.includes(session?.id?.toString() ?? "")
							);
							checkSession = event.sessions?.length === 0;
							if (checkSession) {
								setModal({ isOpen: false });
							} else {
								setModal({
									isOpen: true,
									title: "セッションをキャンセル",
									cancelText: "閉じる",
									confirmText: "選択したセッションをキャンセルする",
									hideConfirmButton: false,
									disabledConfirm: true,
									onConfirm: () => {
										setModal({
											isOpen: true,
											title: "セッションをキャンセル",
											cancelText: "閉じる",
											confirmText: "選択したセッションをキャンセルする",
											hideConfirmButton: false,
											onConfirm: () =>
												setState((preState) => ({
													...preState,
													actionRemove: true,
													actionId: event?.id ?? 0,
												})),
											showButton: true,
											childrenSession: (
												<div className="mt-3 -font-size">
													<h5 style={{ fontWeight: 800 }}>
														キャンセルされますと、本講演の視聴・参加ができなくなります。
													</h5>
													<br />
													再度ご視聴・ご参加を希望される場合は、あらためてお申し込みが必要です。
													<br />
													内容をご確認のうえ、キャンセルをご希望の場合は「キャンセルする」ボタンを押してください。
												</div>
											),
										});
									},
									showButton: true,
									childrenSession: (
										<>
											<ul className="checkbox-list-modal">
												{event?.sessions && event?.sessions?.length > 0 ? (
													<p className="-font-size">
														キャンセルしたいセッションにチェックを入れて、「選択したセッションをキャンセル」ボタンを押してください。
													</p>
												) : (
													<p className="-font-size">
														予約済みのセッションはありません。
													</p>
												)}

												<p className="txt-red -font-size">
													{sessionIds.length}
													件のセッションをキャンセルしました。
												</p>
												<div>
													{event.sessions
														?.map((session) => ({
															...session,
															lectures: session.lectures
																?.slice()
																.sort((a, b) => {
																	const timeA = a.start_at
																		? new Date(a.start_at).getTime()
																		: 0;
																	const timeB = b.start_at
																		? new Date(b.start_at).getTime()
																		: 0;
																	return timeA - timeB;
																}),
														}))
														.sort((a, b) => {
															const venueA = a.venue || "";
															const venueB = b.venue || "";
															if (venueA !== venueB)
																return venueA.localeCompare(venueB);

															const blockA = a.block || "";
															const blockB = b.block || "";
															const numBlockA = parseInt(blockA, 10);
															const numBlockB = parseInt(blockB, 10);
															if (!isNaN(numBlockA) && !isNaN(numBlockB)) {
																if (numBlockA !== numBlockB)
																	return numBlockA - numBlockB;
															} else {
																if (blockA !== blockB)
																	return blockA.localeCompare(blockB);
															}
															const startA = a.lectures?.[0]?.start_at
																? new Date(a.lectures[0].start_at).getTime()
																: 0;
															const startB = b.lectures?.[0]?.start_at
																? new Date(b.lectures[0].start_at).getTime()
																: 0;
															return startA - startB;
														})
														.map((session) => {
															const { lectures, schedules } = session;
															const allPast = lectures?.every((lecture) =>
																isLectureExpired(lecture)
															);
															return (
																<ul className="checkbox-list-modal">
																	<li
																		key={session?.id}
																		className="-font-size -form-control"
																	>
																		<FormControlLabel
																			className="lable-class"
																			control={
																				<>
																					<Checkbox
																						value={session?.id}
																						onChange={(event) =>
																							handleChangeSessionIds(
																								event,
																								session?.id?.toString() ?? ""
																							)
																						}
																						defaultChecked={allPast}
																						disabled={allPast}
																						sx={{
																							"&.Mui-checked": {
																								color: "blue",
																								fontSize: 25,
																							},
																							"& .MuiSvgIcon-root": {
																								fontSize: "25px",
																							},
																							"&.Mui-disabled": {
																								color: "gray",
																							},
																							alignSelf: "flex-start",
																						}}
																					/>
																					<div
																						style={{
																							color: allPast
																								? "gray"
																								: undefined,
																						}}
																						className="div-class"
																					>
																						{lectures?.map((lecture) =>
																							_renderItemSchedules(
																								lecture,
																								schedules ?? [],
																								session
																							)
																						)}
																					</div>
																				</>
																			}
																			label={""}
																		/>
																	</li>
																</ul>
															);
														})}
												</div>
											</ul>
										</>
									),
								});
							}
						}

						return event;
					})
					.filter((event) => event.sessions && event.sessions?.length > 0);

				if (
					checkSession &&
					events.length % 5 === 1 &&
					Number(current_page) > 1
				) {
					handlePageChange(Number(current_page) - 1);
				} else {
					handlePageChange(Number(current_page));
				}
				setState((preState) => ({
					...preState,
					events: updatedEvents,
					sessionIds: [],
				}));
			}
		} catch (err) {
			return err;
		} finally {
			setStateValue("loader", false);
		}
	};

	const handleChangeSessionIds = (
		event: React.ChangeEvent<HTMLInputElement>,
		sessionId: string
	) => {
		setState((prevState) => {
			const updatedSessionIds = event.target.checked
				? [...prevState.sessionIds, sessionId]
				: prevState.sessionIds.filter((id) => id !== sessionId);

			setModalDisabledConfirm?.(updatedSessionIds.length === 0);
			return {
				...prevState,
				sessionIds: updatedSessionIds,
			};
		});
	};

	const handleGetEventDetail = async (eventId: number) => {
		setStateValue("loader", true);
		try {
			const result = await findEventMicroCMS(eventId.toString());
			if (result.status === 200) {
				const event_id = result.data.data.contents[0].id;
				window.location.href = event_id
					? `${appConfig.SPA_EVENT_WEB}/marketing/event/${event_id}`
					: `${appConfig.SPA_EVENT_WEB}/marketing/event`;
			}
		} catch (err) {
			return err;
		} finally {
			setStateValue("loader", false);
		}
	};

	const renderEventDates = (event_dates: string[]) => {
		const currentDate = new Date();
		const isEventOngoing = event_dates.some((date) => {
			const eventDate = new Date(date);
			return eventDate.toDateString() === currentDate.toDateString();
		});

		return (
			<div>{isEventOngoing && <div className="ongoing-label">開催中</div>}</div>
		);
	};

	const renderItemList = () => {
		return events.map((event, index) => {
			const { sessions, id } = event;
			const startDate = new Date(event?.registration_dates?.[0] ?? "");
			const endDate = new Date(event?.registration_dates?.[1] ?? "");
			const registration_end_date = new Date(
				event?.registration_dates?.[1] ?? ""
			);
			const startStr = `${startDate.getFullYear()}.${(startDate.getMonth() + 1)
				.toString()
				.padStart(2, "0")}.${startDate.getDate().toString().padStart(2, "0")}`;
			const endStr = `${endDate.getFullYear()}.${(endDate.getMonth() + 1)
				.toString()
				.padStart(2, "0")}.${endDate.getDate().toString().padStart(2, "0")}`;
			// const registrationDates =
			// startDate.getMonth() === endDate.getMonth()
			//   ? `${startStr}-${endDate.getDate().toString().padStart(2, "0")}`
			//   : `${startStr}-${endStr}`;
			const currentDate = new Date();
			endDate.setHours(0, 0, 0, 0);
			currentDate.setHours(0, 0, 0, 0);
			const isRegistrationExpired = endDate < currentDate;
			const groupedSessions = groupSessionsByDate(sessions || []);
			const registrationEndDate = new Date(event.registration_dates?.[1] ?? "");
			const hasCancelableSessions = event.sessions?.some((session) =>
				session.lectures?.some((lecture) => !isLectureExpired(lecture))
			);

			const shouldHideConfirmButton =
				isRegistrationExpired ||
				!hasCancelableSessions ||
				sessionIds.length === 0;

			return (
				<div className="box" key={index}>
					<div className="d-flex session-wrapper">
						<div>
							{renderEventDates(event.event_dates ?? [])}
							<div className="-head">
								<div className="txt-14 txt-color">{event.name}</div>
							</div>

							<div className="-content">
								{event.plans.plan_name && (
									<div className="txt-loud txt-14 mb-2 text-custom-gray">
										予約済みのチケット : {event.plans.plan_name}
									</div>
								)}
								<div className="txt-loud txt-14 mb-2 ms-2 text-custom-gray ">
									来場予約済みのセッション
								</div>
								<div className="mb-3 ms-2">
									{Object.entries(groupedSessions)
										.sort((a, b) => a[0].localeCompare(b[0]))
										.map(([date, sessions]) => {
											const allLecturesExpired = sessions.every((session) =>
												session.lectures?.every((lecture) =>
													isLectureExpired(lecture)
												)
											);
											return (
												<div key={date}>
													<div
														className={`txt-14 mt-3 -font-size ${
															allLecturesExpired ? "txt-gray" : ""
														}`}
													>
														<img
															className={`-icon-date ${
																allLecturesExpired ? "-icon-gray" : ""
															}`}
															src="/images/ico_date.svg"
														/>
														{date}
													</div>
													{sessions
														.map((session) => ({
															...session,
															lectures: session.lectures
																?.slice()
																.sort((a, b) => {
																	const timeA = a.start_at
																		? new Date(a.start_at).getTime()
																		: 0;
																	const timeB = b.start_at
																		? new Date(b.start_at).getTime()
																		: 0;
																	return timeA - timeB;
																}),
														}))
														.sort((a, b) => {
															const venueA = a.venue || "";
															const venueB = b.venue || "";
															if (venueA !== venueB)
																return venueA.localeCompare(venueB);

															const blockA = a.block || "";
															const blockB = b.block || "";
															const numBlockA = parseInt(blockA, 10);
															const numBlockB = parseInt(blockB, 10);
															if (!isNaN(numBlockA) && !isNaN(numBlockB)) {
																if (numBlockA !== numBlockB)
																	return numBlockA - numBlockB;
															} else {
																if (blockA !== blockB)
																	return blockA.localeCompare(blockB);
															}
															const startA = a.lectures?.[0]?.start_at
																? new Date(a.lectures[0].start_at).getTime()
																: 0;
															const startB = b.lectures?.[0]?.start_at
																? new Date(b.lectures[0].start_at).getTime()
																: 0;
															return startA - startB;
														})
														.map((session) => {
															const { lectures, schedules } = session;
															return (
																<div className="mt-1" key={session.id}>
																	{lectures?.map((lecture) =>
																		_renderItemSessions(
																			lecture,
																			schedules ?? [],
																			session
																		)
																	)}
																	{checkShowButtonVideo(
																		event.ondemand ?? true,
																		session.video_available_at,
																		session.video_disable_at,
																		registration_end_date,
																		session.video_player_tag
																	) == 1 ? (
																		<div
																			className="mt-2 btn-video"
																			style={{ cursor: "pointer" }}
																			onClick={() => ShowVideo(session.id ?? 0)}
																		>
																			セミナー視聴・資料ダウンロード
																		</div>
																	) : checkShowButtonVideo(
																			event.ondemand ?? true,
																			session.video_available_at,
																			session.video_disable_at,
																			registration_end_date,
																			session.video_player_tag
																	  ) == 2 ? (
																		<div
																			className="mt-2 -btn-video"
																			aria-disabled
																		>
																			セミナー・資料 準備中
																		</div>
																	) : checkShowButtonVideo(
																			event.ondemand ?? true,
																			session.video_available_at,
																			session.video_disable_at,
																			registration_end_date,
																			session.video_player_tag
																	  ) == 4 ? (
																		<div
																			className="mt-2 -btn-video"
																			aria-disabled
																		>
																			セミナー・資料 公開前
																		</div>
																	) : (
																		<></>
																	)}
																</div>
															);
														})}
												</div>
											);
										})}
								</div>
								{event.plans && Object.keys(event.plans).length > 0 && (
									<div className="txt-loud txt-14 mb-2 text-custom-gray">
										※セッションをすべてキャンセルされた場合、イベント自体のお申込みもキャンセルとなります。
										<br />
										なお、有料パスのキャンセルやプラン変更に伴う返金は、原則として承っておりませんのでご了承ください。
									</div>
								)}
							</div>
						</div>
						{(event.onplace_or_online === 1 || event.onplace_or_online === 3) &&
						(searchParams?.current_event === true ||
							searchParams?.current_event === undefined) ? (
							<div className="ms-auto">
								<Dialog.Root>
									<Dialog.Trigger asChild>
										<div
											style={{
												width: 258,
												cursor: "pointer",
												padding: "8px",
												background: "white",
												display: "inline-block",
												textAlign: "center",
											}}
										>
											<QRCode
												value={formatDigits(event.user_qr_id ?? 0, true)}
												size={250}
											/>
											<div style={{ marginTop: 8, fontSize: 16 }}>
												{formatDigits(event.user_qr_id ?? 0, false)}
											</div>
										</div>
									</Dialog.Trigger>

									<Dialog.Portal>
										<Dialog.Overlay
											style={{
												position: "fixed",
												inset: 0,
												backgroundColor: "rgba(0, 0, 0, 0.5)",
											}}
										/>
										<Dialog.Content
											style={{
												position: "fixed",
												top: "50%",
												left: "50%",
												transform: "translate(-50%, -50%)",
												background: "white",
												padding: 32,
												borderRadius: 8,
											}}
										>
											<QRCode
												value={formatDigits(event.user_qr_id ?? 0, true)}
												size={300}
											/>
											<Dialog.Close
												style={{
													position: "absolute",
													top: 8,
													right: 8,
													background: "transparent",
													border: "none",
													fontSize: 20,
													cursor: "pointer",
												}}
											>
												✕
											</Dialog.Close>
										</Dialog.Content>
									</Dialog.Portal>
								</Dialog.Root>
							</div>
						) : (
							""
						)}
					</div>
					{searchParams?.current_event !== false && (
						<div className="btn-group" key={index}>
							<div
								className={`action ${isRegistrationExpired ? "disabled" : ""}`}
								onClick={() => {
									if (!isRegistrationExpired) {
										setModal({
											isOpen: true,
											title: "セッションをキャンセル",
											cancelText: "閉じる",
											confirmText: "選択したセッションをキャンセルする",
											hideConfirmButton: false,
											disabledConfirm: shouldHideConfirmButton,
											onConfirm: () => {
												setModal({
													isOpen: true,
													title: "セッションをキャンセル",
													cancelText: "閉じる",
													confirmText: "選択したセッションをキャンセルする",
													hideConfirmButton: false,
													onConfirm: () =>
														setState((preState) => ({
															...preState,
															actionRemove: true,
															actionId: event?.id ?? 0,
														})),
													showButton: true,
													childrenSession: (
														<div className="mt-3 -font-size">
															<h5 style={{ fontWeight: 800 }}>
																キャンセルされますと、本講演の視聴・参加ができなくなります。
															</h5>
															<br />
															再度ご視聴・ご参加を希望される場合は、あらためてお申し込みが必要です。
															<br />
															内容をご確認のうえ、キャンセルをご希望の場合は「キャンセルする」ボタンを押してください。
														</div>
													),
												});
											},
											showButton: true,
											childrenSession: (
												<div>
													<p className="mt-3 -font-size">
														キャンセルしたいセッションにチェックを入れて、「選択したセッションをキャンセル」ボタンを押してください。
													</p>
													<div>
														{(() => {
															const groupedSessions = groupSessionsByDate(
																sessions || []
															);

															return Object.entries(groupedSessions)
																.sort((a, b) => a[0].localeCompare(b[0]))
																.map(([date, groupedSessionList]) => {
																	const hasLectures = groupedSessionList.some(
																		(session) =>
																			session.lectures &&
																			session.lectures.length > 0
																	);
																	if (!hasLectures) return null;

																	const allLecturesExpired =
																		groupedSessionList.every((session) =>
																			session.lectures?.every((lecture) =>
																				isLectureExpired(lecture)
																			)
																		);

																	return (
																		<ul
																			className="checkbox-list-modal"
																			key={date}
																		>
																			<div
																				className={`txt-14 mt-3 ${
																					allLecturesExpired ? "txt-gray" : ""
																				}`}
																			>
																				{date}
																			</div>
																			{groupedSessionList
																				.map((session) => ({
																					...session,
																					lectures: session.lectures
																						?.slice()
																						.sort((a, b) => {
																							const timeA = a.start_at
																								? new Date(a.start_at).getTime()
																								: 0;
																							const timeB = b.start_at
																								? new Date(b.start_at).getTime()
																								: 0;
																							return timeA - timeB;
																						}),
																				}))
																				.sort((a, b) => {
																					const venueA = a.venue || "";
																					const venueB = b.venue || "";
																					if (venueA !== venueB)
																						return venueA.localeCompare(venueB);

																					const blockA = a.block || "";
																					const blockB = b.block || "";
																					const numBlockA = parseInt(
																						blockA,
																						10
																					);
																					const numBlockB = parseInt(
																						blockB,
																						10
																					);
																					if (
																						!isNaN(numBlockA) &&
																						!isNaN(numBlockB)
																					) {
																						if (numBlockA !== numBlockB)
																							return numBlockA - numBlockB;
																					} else {
																						if (blockA !== blockB)
																							return blockA.localeCompare(
																								blockB
																							);
																					}

																					const startA = a.lectures?.[0]
																						?.start_at
																						? new Date(
																								a.lectures[0].start_at
																						  ).getTime()
																						: 0;
																					const startB = b.lectures?.[0]
																						?.start_at
																						? new Date(
																								b.lectures[0].start_at
																						  ).getTime()
																						: 0;
																					return startA - startB;
																				})
																				.map((session, index) => {
																					const { lectures, schedules } =
																						session;
																					if (
																						!lectures ||
																						lectures.length === 0
																					)
																						return null;

																					const allPast = lectures.every(
																						(lecture) =>
																							isLectureExpired(lecture)
																					);

																					return (
																						<li
																							key={session?.id}
																							className={`${
																								index >= 0
																									? "-font-size -form-control"
																									: ""
																							}`}
																						>
																							<FormControlLabel
																								className="lable-class"
																								control={
																									<>
																										<Checkbox
																											value={session?.id}
																											onChange={(event) =>
																												handleChangeSessionIds(
																													event,
																													session?.id?.toString() ??
																														""
																												)
																											}
																											defaultChecked={allPast}
																											disabled={allPast}
																											sx={{
																												"&.Mui-checked": {
																													color: "blue",
																													fontSize: 25,
																												},
																												"& .MuiSvgIcon-root": {
																													fontSize: "25px",
																												},
																												"&.Mui-disabled": {
																													color: "gray",
																												},
																												alignSelf: "flex-start",
																											}}
																										/>
																										<div className="div-class">
																											{lectures.map((lecture) =>
																												_renderCancelItemSchedules(
																													lecture,
																													schedules ?? [],
																													session
																												)
																											)}
																										</div>
																									</>
																								}
																								label={""}
																							/>
																						</li>
																					);
																				})}
																		</ul>
																	);
																});
														})()}
													</div>
												</div>
											),
										});
									}
								}}
							>
								セッションをキャンセル
								<span className="separator-1"> | </span>
								<span className="icon">
									<i className="fa-solid fa-minus"></i>
								</span>
							</div>
							<div
								className={`-action ${isRegistrationExpired ? "disabled" : ""}`}
								onClick={() =>
									!isRegistrationExpired && handleGetEventDetail(event.id ?? 0)
								}
							>
								セッションを追加
								<span className="separator"> | </span>
								<span className="icon">
									<i className="fa-solid fa-plus"></i>
								</span>
							</div>
						</div>
					)}
					<div className="btn-group">
						<div
							className="action-view"
							onClick={() => {
								handleGetEventDetail(id ?? 0);
							}}
						>
							イベントの詳細を見る
							<img className="" src="/images/icon_12px.svg" />
						</div>
					</div>
				</div>
			);
		});
	};
	const handlePageChange = (page: number) => {
		setState((preState) => ({
			...preState,
			current_page: page,
			searchParams: {
				...preState.searchParams,
				page: page.toString(),
			},
		}));
	};

	return (
		<div className="pages__events">
			<div className="d-flex">
				<h1 className="h1-title">My Page</h1>
				<span className="h1-span">マイページ</span>
			</div>
			<div className="h-title d-flex">
				<img className="-icon" src="/images/icon_16px.svg" />
				イベント一覧
				<hr className="line" />
			</div>
			<div className="tb-header mb-3">
				<div
					onClick={() => {
						setState((preState) => ({
							...preState,
							searchParams: {
								...preState.searchParams,
								current_event: true,
							},
						}));
					}}
					className={`-item ${
						searchParams?.current_event === undefined ||
						searchParams?.current_event
							? "active"
							: ""
					} `}
				>
					申込受付中・開催中
				</div>
				<div
					onClick={() => {
						setState((preState) => ({
							...preState,
							searchParams: {
								...preState.searchParams,
								current_event: false,
							},
						}));
					}}
					className={`-item ${
						searchParams?.current_event === false ? "active" : ""
					} `}
				>
					開催終了
				</div>
			</div>

			{events.length > 0 ? (
				renderItemList()
			) : (
				<div className="mt-5 text-center"> データがありません。</div>
			)}

			{events.length > 0 && (
				<Pagination
					currentPage={current_page ?? 1}
					lastPage={total_pages ?? 1}
					onPageChange={handlePageChange}
				/>
			)}
		</div>
	);
};

export default Events;
